import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button, Input, Label, toast } from '@employer-on-record/ui';
import type { EmployeeDTO } from '@employer-on-record/shared';
import { adminApi, employeesAdminApi } from '@/services/api';

type Mode = 'create' | 'edit';

interface Props {
	open: boolean;
	mode: Mode;
	companyId: string;
	companyName: string;
	employee: EmployeeDTO | null;
	onClose: () => void;
}

const blank = {
	employeeNumber: '',
	fullName: '',
	email: '',
	department: '',
	position: '',
	startDate: new Date().toISOString().slice(0, 10),
	password: '',
	status: 'active' as 'active' | 'on_leave' | 'terminated' | 'probation'
};

export function EmployeeFormDrawer({ open, mode, companyId, companyName, employee, onClose }: Props) {
	const qc = useQueryClient();
	const [form, setForm] = useState(blank);

	useEffect(() => {
		if (mode === 'edit' && employee) {
			setForm({
				employeeNumber: employee.employeeNumber,
				fullName: employee.fullName,
				email: employee.email,
				department: employee.department ?? '',
				position: employee.position ?? '',
				startDate: employee.startDate.slice(0, 10),
				password: '',
				status: employee.status
			});
		} else if (open) {
			setForm(blank);
		}
	}, [mode, employee, open]);

	const submit = useMutation({
		mutationFn: () => {
			if (mode === 'edit' && employee) {
				return employeesAdminApi.update(employee.id, {
					employeeNumber: form.employeeNumber,
					fullName: form.fullName,
					email: form.email,
					department: form.department || null,
					position: form.position || null,
					startDate: form.startDate,
					status: form.status
				});
			}
			return adminApi.createEmployee({
				companyId,
				employeeNumber: form.employeeNumber,
				fullName: form.fullName,
				email: form.email,
				department: form.department || undefined,
				position: form.position || undefined,
				startDate: form.startDate,
				password: form.password || undefined
			});
		},
		onSuccess: (res: unknown) => {
			const pwd = mode === 'create'
				? (res as { data: { initialPassword: string | null } }).data.initialPassword
				: null;
			toast(mode === 'create'
				? `Employee created${pwd ? ` · password: ${pwd}` : ''}`
				: 'Employee updated');
			qc.invalidateQueries({ queryKey: ['admin-company-employees', companyId] });
			onClose();
		},
		onError: (err) => toast((err as Error).message, 'destructive')
	});

	if (!open) return null;

	return (
		<div className="fixed inset-0 z-40 flex">
			<div className="flex-1 bg-black/30" onClick={onClose} />
			<div className="w-[420px] bg-card border-l overflow-y-auto">
				<div className="px-5 py-4 border-b flex items-center">
					<h3 className="font-semibold">
						{mode === 'create' ? 'Add employee' : `Edit ${employee?.fullName}`} · {companyName}
					</h3>
					<button onClick={onClose} className="ml-auto p-1 rounded hover:bg-background"><X className="h-4 w-4" /></button>
				</div>
				<form onSubmit={(e) => { e.preventDefault(); submit.mutate(); }} className="p-5 grid gap-3">
					<div className="grid gap-1.5">
						<Label htmlFor="emp-number">Employee number</Label>
						<Input id="emp-number" value={form.employeeNumber} onChange={(e) => setForm(f => ({ ...f, employeeNumber: e.target.value }))} required />
					</div>
					<div className="grid gap-1.5">
						<Label htmlFor="emp-name">Full name</Label>
						<Input id="emp-name" value={form.fullName} onChange={(e) => setForm(f => ({ ...f, fullName: e.target.value }))} required />
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div className="grid gap-1.5">
							<Label htmlFor="emp-email">Email</Label>
							<Input id="emp-email" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} required />
						</div>
						<div className="grid gap-1.5">
							<Label htmlFor="emp-start">Start date</Label>
							<Input id="emp-start" type="date" value={form.startDate} onChange={(e) => setForm(f => ({ ...f, startDate: e.target.value }))} required />
						</div>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div className="grid gap-1.5">
							<Label htmlFor="emp-dept">Department</Label>
							<Input id="emp-dept" value={form.department} onChange={(e) => setForm(f => ({ ...f, department: e.target.value }))} />
						</div>
						<div className="grid gap-1.5">
							<Label htmlFor="emp-pos">Position</Label>
							<Input id="emp-pos" value={form.position} onChange={(e) => setForm(f => ({ ...f, position: e.target.value }))} />
						</div>
					</div>
					{mode === 'edit' && (
						<div className="grid gap-1.5">
							<Label htmlFor="emp-status">Status</Label>
							<select
								id="emp-status"
								className="border rounded-md px-3 py-2 text-sm bg-white"
								value={form.status}
								onChange={(e) => setForm(f => ({ ...f, status: e.target.value as typeof blank.status }))}
							>
								<option value="active">Active</option>
								<option value="probation">Probation</option>
								<option value="on_leave">On leave</option>
								<option value="terminated">Terminated</option>
							</select>
						</div>
					)}
					{mode === 'create' && (
						<div className="grid gap-1.5">
							<Label htmlFor="emp-pass">Initial password (leave blank for default)</Label>
							<Input id="emp-pass" type="text" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} placeholder="changeme1234" />
						</div>
					)}
					<div className="flex gap-2 mt-4">
						<Button type="button" variant="secondary" onClick={onClose} className="flex-1 justify-center">Cancel</Button>
						<Button type="submit" className="flex-1 justify-center" disabled={submit.isPending}>
							{submit.isPending ? 'Saving…' : mode === 'create' ? 'Create employee' : 'Save'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}
